// ============================================================
//  NumberPath — Mini-game 2: Navigate a number line to the target
// ============================================================
import { CS } from '../../constants/Colors.js';
import { PlaceValueQuestion } from '../../learning/math/PlaceValue.js';
import { audio } from '../../audio/AudioManager.js';
import { SaveManager } from '../../state/SaveManager.js';
import { XP_CORRECT, XP_HINT } from '../../learning/math/PlaceValue.js';
import type { MiniGameCallback } from './CrystalBuilder.js';

export class NumberPath {
  private scene: Phaser.Scene;
  private container!: Phaser.GameObjects.Container;
  private question: PlaceValueQuestion;
  private onComplete: MiniGameCallback;
  private W: number;
  private H: number;

  private currentPos: number;
  private target: number;
  private start: number;

  private orbGfx!: Phaser.GameObjects.Graphics;
  private orbX!: number;
  private posText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private pathLineG!: Phaser.GameObjects.Graphics;

  private lineStartX!: number;
  private lineEndX!: number;
  private lineY!: number;
  private numRange!: number;
  private attempts = 0;
  private hintUsed = false;

  constructor(scene: Phaser.Scene, question: PlaceValueQuestion, onComplete: MiniGameCallback) {
    this.scene = scene;
    this.question = question;
    this.onComplete = onComplete;
    this.W = scene.scale.width;
    this.H = scene.scale.height;
    this.start = question.pathStart ?? 100;
    this.target = question.pathEnd ?? 150;
    this.currentPos = this.start;
    this.create();
  }

  private create() {
    const { W, H } = this;
    this.container = this.scene.add.container(0, 0);

    // Overlay + Panel
    const overlay = this.scene.add.graphics();
    overlay.fillStyle(0x000010, 0.88); overlay.fillRect(0, 0, W, H);
    const panel = this.scene.add.graphics();
    panel.fillStyle(0x0d0230, 0.97); panel.fillRoundedRect(W * 0.06, H * 0.05, W * 0.88, H * 0.9, 20);
    panel.lineStyle(1.5, 0x3d2060, 0.8); panel.strokeRoundedRect(W * 0.06, H * 0.05, W * 0.88, H * 0.9, 20);

    const title = this.scene.add.text(W * 0.5, H * 0.1, '🛤️ Number Path', {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '22px', fontStyle: 'bold', color: '#00e5ff',
      stroke: '#000', strokeThickness: 3,
    }).setOrigin(0.5, 0.5);

    const instr = this.scene.add.text(W * 0.5, H * 0.17,
      `Start at ${this.start}. Use +10 and +1 to reach  ${this.target}!`, {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '15px', color: '#ccddff', align: 'center',
    }).setOrigin(0.5, 0.5);

    // Target badge
    const tBg = this.scene.add.graphics();
    tBg.fillStyle(0x1a0a3e, 1); tBg.fillRoundedRect(W * 0.36, H * 0.22, W * 0.28, H * 0.09, 12);
    tBg.lineStyle(2, 0x00e5ff, 0.8); tBg.strokeRoundedRect(W * 0.36, H * 0.22, W * 0.28, H * 0.09, 12);
    const tLabel = this.scene.add.text(W * 0.5, H * 0.226, 'REACH', {
      fontFamily: '"Nunito", Arial, sans-serif', fontSize: '11px', color: '#00e5ff',
    }).setOrigin(0.5, 0);
    const tNum = this.scene.add.text(W * 0.5, H * 0.255, `${this.target}`, {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '32px', fontStyle: 'bold', color: '#ffffff',
      stroke: '#00e5ff', strokeThickness: 3,
    }).setOrigin(0.5, 0.5);

    // Number line
    this.lineStartX = W * 0.1;
    this.lineEndX   = W * 0.9;
    this.lineY      = H * 0.52;
    this.numRange   = this.target - this.start + Math.max(10, Math.floor((this.target - this.start) * 0.15));

    this.pathLineG = this.scene.add.graphics();
    this.drawNumberLine();

    // Orb (player position)
    this.orbGfx = this.scene.add.graphics();
    this.orbX = this.posToX(this.start);
    this.drawOrb(this.orbX, this.lineY);

    // Position display
    this.posText = this.scene.add.text(W * 0.5, H * 0.61, `You are at: ${this.start}`, {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '18px', fontStyle: 'bold', color: '#ffd700',
      stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5, 0.5);

    // Control buttons
    const btns = [
      { label: '+10', color: '#00e5ff', cb: () => this.move(10) },
      { label: '+1',  color: '#c080ff', cb: () => this.move(1) },
      { label: '−1',  color: '#ffaa44', cb: () => this.move(-1) },
      { label: '−10', color: '#ff8888', cb: () => this.move(-10) },
    ];
    const btnXs = [W * 0.2, W * 0.38, W * 0.62, W * 0.8];
    const btnObjs = btns.map((b, i) => this.makeButton(btnXs[i], H * 0.71, b.label, b.color, b.cb));

    // Hint & Reset
    const hintBtn  = this.makeButton(W * 0.35, H * 0.82, '💡 Hint',   '#ffeb3b', () => this.showHint());
    const resetBtn = this.makeButton(W * 0.65, H * 0.82, '↺ Reset',   '#ff8c00', () => this.resetPos());

    this.feedbackText = this.scene.add.text(W * 0.5, H * 0.91, '', {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '15px', color: '#00e676', stroke: '#000', strokeThickness: 2, align: 'center',
    }).setOrigin(0.5, 0.5);

    this.container.add([
      overlay, panel, title, instr,
      tBg, tLabel, tNum,
      this.pathLineG, this.orbGfx,
      this.posText,
      ...btnObjs, hintBtn, resetBtn,
      this.feedbackText,
    ]);
  }

  private drawNumberLine() {
    const g = this.pathLineG;
    g.clear();
    const { lineStartX: lx, lineEndX: ex, lineY: ly, start, numRange } = this;

    // Track
    g.lineStyle(4, 0x1a0a3e, 1); g.lineBetween(lx, ly, ex, ly);
    g.lineStyle(3, 0x3d2060, 0.9); g.lineBetween(lx, ly, ex, ly);

    // Markers every 10 units in the range
    const step10 = Math.max(1, Math.floor(numRange / 10));
    const displayEnd = start + numRange;

    for (let n = start; n <= displayEnd; n++) {
      const x = this.posToX(n);
      if (n % 10 === 0) {
        // Major tick
        g.lineStyle(2, 0x7b2fff, 0.8);
        g.lineBetween(x, ly - 12, x, ly + 12);
        g.fillStyle(0x888aaa, 1);
        const nText = this.scene.add.text(x, ly + 20, `${n}`, {
          fontFamily: '"Nunito", Arial, sans-serif', fontSize: '10px', color: '#888aaa',
        }).setOrigin(0.5, 0);
        this.container.add(nText);
      } else if (n % 5 === 0) {
        // Minor tick
        g.lineStyle(1, 0x3d2060, 0.6);
        g.lineBetween(x, ly - 6, x, ly + 6);
      }
    }

    // Start marker
    for (let i = 3; i >= 1; i--) {
      g.fillStyle(0x7b2fff, 0.08 * i); g.fillCircle(this.posToX(start), ly, 16 + i * 4);
    }
    g.fillStyle(0x7b2fff, 0.9); g.fillCircle(this.posToX(start), ly, 12);
    g.fillStyle(0xffffff, 0.3); g.fillCircle(this.posToX(start) - 3, ly - 3, 5);
    const sText = this.scene.add.text(this.posToX(start), ly - 28, `${start}`, {
      fontFamily: '"Nunito", Arial, sans-serif', fontSize: '12px', color: '#c080ff',
      stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5, 0.5);
    this.container.add(sText);

    // Target marker (pulsing star)
    const tx = this.posToX(this.target);
    for (let i = 5; i >= 1; i--) {
      g.fillStyle(0x00e5ff, 0.07 * i); g.fillCircle(tx, ly, 18 + i * 5);
    }
    g.fillStyle(0x00e5ff, 0.9); g.fillCircle(tx, ly, 16);
    g.fillStyle(0xffffff, 0.5); g.fillCircle(tx - 4, ly - 4, 6);
    const tText = this.scene.add.text(tx, ly - 34, `★ ${this.target}`, {
      fontFamily: '"Nunito", Arial, sans-serif', fontSize: '13px', fontStyle: 'bold',
      color: '#00e5ff', stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5, 0.5);
    this.container.add(tText);
    // Pulse target
    this.scene.tweens.add({ targets: tText, y: this.lineY - 40, duration: 800, ease: 'Sine.easeInOut', yoyo: true, repeat: -1 });
  }

  private drawOrb(x: number, y: number) {
    const g = this.orbGfx;
    g.clear();
    for (let i = 5; i >= 1; i--) {
      g.fillStyle(0xffd700, 0.08 * i); g.fillCircle(x, y, 18 + i * 5);
    }
    g.fillStyle(0xffa500, 1); g.fillCircle(x, y, 16);
    g.fillStyle(0xffd700, 0.9); g.fillCircle(x, y, 12);
    g.fillStyle(0xffffff, 0.6); g.fillCircle(x - 4, y - 4, 6);
    g.fillStyle(0xffffff, 0.3); g.fillCircle(x - 6, y - 6, 3);
  }

  private posToX(pos: number): number {
    const pct = (pos - this.start) / this.numRange;
    return this.lineStartX + pct * (this.lineEndX - this.lineStartX);
  }

  private move(delta: number) {
    const newPos = this.currentPos + delta;
    if (newPos < this.start - 20 || newPos > this.target + 20) return;

    this.currentPos = newPos;
    const newX = this.posToX(this.currentPos);
    audio.click();

    // Animate orb movement
    this.scene.tweens.add({
      targets: { x: this.orbX },
      x: newX,
      duration: 200,
      ease: 'Sine.easeOut',
      onUpdate: (tween) => {
        const v = tween.targets[0] as { x: number };
        this.orbX = v.x;
        this.drawOrb(this.orbX, this.lineY);
      },
      onComplete: () => {
        this.orbX = newX;
        this.posText.setText(`You are at: ${this.currentPos}`);

        if (this.currentPos === this.target) {
          this.onReached();
        } else if (this.currentPos > this.target) {
          this.feedbackText.setStyle({ color: '#ff8888' });
          this.feedbackText.setText(`Too far! You passed ${this.target}. Try going back.`);
        } else {
          const remaining = this.target - this.currentPos;
          this.feedbackText.setStyle({ color: '#ccddff' });
          this.feedbackText.setText(`${remaining} more to go!`);
        }
      },
    });
  }

  private resetPos() {
    this.currentPos = this.start;
    this.orbX = this.posToX(this.start);
    this.drawOrb(this.orbX, this.lineY);
    this.posText.setText(`You are at: ${this.start}`);
    this.feedbackText.setText('');
    audio.click();
  }

  private showHint() {
    this.hintUsed = true;
    SaveManager.addXP(XP_HINT);
    const needed10 = Math.floor((this.target - this.currentPos) / 10);
    const needed1  = (this.target - this.currentPos) % 10;
    this.feedbackText.setStyle({ color: '#ffeb3b' });
    this.feedbackText.setText(
      `Hint: Press +10 ${needed10} time${needed10 !== 1 ? 's' : ''}, then +1 ${needed1} time${needed1 !== 1 ? 's' : ''}!`,
    );
    audio.hint();
  }

  private onReached() {
    this.attempts++;
    SaveManager.recordAnswer(this.question.skillId, true);
    SaveManager.addXP(this.hintUsed ? XP_HINT : XP_CORRECT);
    audio.correct();

    this.feedbackText.setStyle({ color: '#00e676' });
    this.feedbackText.setText('✓ You reached the target! The path lights up!');

    // Burst
    try {
      const emitter = this.scene.add.particles(this.orbX, this.lineY, 'particle_cyan', {
        speed: { min: 60, max: 180 },
        angle: { min: 0, max: 360 },
        scale: { start: 1.2, end: 0 },
        lifespan: 700,
        quantity: 16,
        tint: [0x00e5ff, 0xffd700, 0xffffff],
      });
      this.scene.time.delayedCall(250, () => emitter.stop());
      this.scene.time.delayedCall(900, () => emitter.destroy());
    } catch { /* ok */ }

    this.scene.time.delayedCall(1400, () => {
      this.destroy();
      this.onComplete(true);
    });
  }

  private makeButton(x: number, y: number, label: string, color: string, cb: () => void): Phaser.GameObjects.Text {
    const btn = this.scene.add.text(x, y, label, {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '18px', fontStyle: 'bold', color,
      backgroundColor: '#0d0230',
      padding: { x: 16, y: 10 },
      stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5, 0.5).setInteractive({ cursor: 'pointer' });
    btn.on('pointerover', () => {
      this.scene.tweens.add({ targets: btn, scaleX: 1.08, scaleY: 1.08, duration: 100 });
      audio.click();
    });
    btn.on('pointerout', () => this.scene.tweens.add({ targets: btn, scaleX: 1, scaleY: 1, duration: 100 }));
    btn.on('pointerdown', cb);
    return btn;
  }

  destroy() { this.container.destroy(); }
}
